import React, { useEffect, useMemo, useState } from 'react';
import { flushActivityBeforeLogout } from '@/hooks/useActivityBeacon/useActivityBeacon';
import { Flex, type MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { GetSupportVideo, Logout } from '@/apis/auth';
import { canUseAiInsightsChat, isZoomlogiSupport } from '@/common/utils/utils';
import { useShipmentPermission } from '@/hooks/useShipmentPermission';
import { trackEvent } from '@/hooks';
import { BeaconEventType } from '@/common/enum';
import theme from '../../assets/theme';
import { clearAllStorageAndState, useToken } from '../../common/helper';
import AIChatDrawer from '../../components/AIChatDrawer';
import { CustomTitle, PreviewHeader } from '../../components';
import { useWebSocket } from '@/context/WebSocketContext';
import { useSelector } from 'react-redux';
import type { RootState } from '../../redux-toolkit/store';
import HelpMenuDropdown from '../Help/HelpMenuDropdown';
import TourVideoModal from '../Help/TourVideoModal';
import CustomHeader from './CustomHeader';
import MobileMenu from './MobileMenu';

declare global {
  interface Window {
    openZoomlogiChat?: () => void;
  }
}

const MOBILE_QUERY = '(max-width: 767px)';

interface ResponsiveHeaderProps {
  useMobileMenu?: boolean;
  onOpenHelpCenter?: () => void;
  helpCenterOpen?: boolean;
  // Insight Chat open state lives in DashboardLayout, shared with Help.
  insightChatOpen?: boolean;
  insightChatQuestion?: string;
  onOpenInsightChat?: () => void;
  onCloseInsightChat?: () => void;
  onInsightChatQuestionSent?: () => void;
}

const ResponsiveHeader: React.FC<ResponsiveHeaderProps> = ({
  useMobileMenu = true,
  onOpenHelpCenter,
  helpCenterOpen = false,
  insightChatOpen = false,
  insightChatQuestion,
  onOpenInsightChat,
  onCloseInsightChat,
  onInsightChatQuestionSent,
}) => {
  const { webSocketService, setSelectedUserID } = useWebSocket() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const { removeToken, setUserId, decodedUserToken, getPreviewState } =
    useToken();

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });
  const [tourOpen, setTourOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string>('');

  const decodedToken = decodedUserToken();
  const { isPreview, previewMode: mode } = getPreviewState();
  const shouldShowRolePreview = useSelector(
    (state: RootState) => state.preview.shouldShowRolePreview
  );
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const showPreviewBanner = !isMobile && (isPreview || shouldShowRolePreview);
  const effectiveMode = isPreview ? mode : shouldShowRolePreview ? 'role' : '';
  const showMessages = useShipmentPermission(decodedToken?.company_name);

  // Chat of any kind (support widget and AI insights) must never be
  // accessible during a preview session — this is independent of the
  // previewed identity's own permissions, since the active token while
  // previewing carries the *target* user's claims, not "no access."
  const showSupportChat =
    !isPreview &&
    !isZoomlogiSupport(decodedToken?.company_name) &&
    decodedToken?.chatbot !== false;

  // Either allowlist (old ai_insights_chatbot or new agentcore_chatbot) is
  // enough to show the icon — they're independent, deliberately separate
  // gates; useAgentCoreChatbot inside AIChatDrawer decides which backend
  // the calls actually go to once open.
  const showAiInsightsChat = canUseAiInsightsChat(decodedToken, isPreview);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(media.matches);
    media.addEventListener('change', handleChange);
    return () => {
      media.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setHelpOpen(false);
      setMobileHelpOpen(false);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    });
    return () =>
      window.removeEventListener('scroll', handleScroll, {
        capture: true,
      });
  }, []);

  // The close callback only shuts Insight Chat, never an open Help panel.
  useEffect(() => {
    onCloseInsightChat?.();
    // Runs on route change only; the callback is recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Force-close the AI chat drawer if a preview session starts while it's
  // already open (e.g. switching preview target from an in-banner control).
  useEffect(() => {
    if (isPreview) onCloseInsightChat?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview]);

  const profileMenuItems: MenuProps['items'] = [
    ...(isZoomlogiSupport(decodedToken?.company_name)
      ? []
      : [{ label: 'Profile', key: '1' }]),
    { label: 'Logout', key: '2' },
  ];

  const clearAllStates = () => {
    removeToken();
    setUserId(null);
    setSelectedUserID?.(null);
    clearAllStorageAndState({ broadcast: true, navigateToLogin: true }).catch(
      console.error
    );
    webSocketService?.disconnect?.();
  };

  const logoutCall = async () => {
    try {
      await flushActivityBeforeLogout();
      const res = await Logout();
      clearAllStates();
      if (!res?.data?.data?.is_success) console.error('Logout failed');
    } catch (err) {
      console.error(err);
      clearAllStates();
    }
  };

  const handleProfileMenuClick = (e: { key: string }) => {
    if (e.key === '1') {
      Promise.resolve(navigate('/settings/profile')).catch(console.error);
    }
    if (e.key === '2') logoutCall().catch(console.error);
  };

  const onWatchTour = async () => {
    setHelpOpen(false);
    setMobileHelpOpen(false);
    setVideoSrc('');
    setTourOpen(true);
    try {
      const res = await GetSupportVideo();
      const videoUrl = res?.data?.data?.data?.video_url;
      if (videoUrl) {
        setVideoSrc(videoUrl);
      }
    } catch (error) {
      console.error('Failed to fetch support video URL:', error);
    }
  };

  const onOpenChat = () => {
    setHelpOpen(false);
    setMobileHelpOpen(false);
    window.openZoomlogiChat?.();
  };

  const openAiInsights = () => {
    trackEvent(BeaconEventType.InsightChatbotOpened);
    setHelpOpen(false);
    setMobileHelpOpen(false);
    onOpenInsightChat?.();
  };

  // Record only a closed -> open transition, not a close or a repeat click
  // while already open.
  const handleHelpOpenChange = (open: boolean) => {
    if (open && !helpOpen) trackEvent(BeaconEventType.HelpOpened);
    setHelpOpen(open);
  };
  // Help icon now opens the Help center; the panel sends help_opened.
  const handleOpenHelpCenter = () => {
    onOpenHelpCenter?.();
  };
  const handleMobileHelpOpenChange = (open: boolean) => {
    if (open && !mobileHelpOpen) trackEvent(BeaconEventType.HelpOpened);
    setMobileHelpOpen(open);
  };

  const helpOverlay = useMemo(() => {
    return (
      <HelpMenuDropdown
        showChat={showSupportChat}
        onWatchTour={onWatchTour}
        onOpenChat={onOpenChat}
        onClose={() => {
          setHelpOpen(false);
          setMobileHelpOpen(false);
        }}
        supportEmail='support@zoomlogi.com'
        mailSubject='Zoomlogi support request'
      />
    );
  }, [showSupportChat]);

  return (
    <>
      {isMobile && useMobileMenu ? (
        <Flex
          align='center'
          justify='space-between'
          style={{ height: 'inherit' }}
        >
          {theme.themeType === 1 ? (
            <CustomTitle
              text={theme.title}
              color='var(--zl-header-title, #020922)'
              fontSize={16}
              fontWeight={600}
              lineHeight='28px'
              marginBottom='0px'
            />
          ) : darkModeEnabled ? (
            <img
              src={theme.whiteLogo}
              width={theme.headerLogoWidth}
              className='header-logo'
              // filter comes from the per-tenant theme config
              // eslint-disable-next-line react/forbid-dom-props
              style={
                theme.whiteLogoFilter
                  ? { filter: theme.whiteLogoFilter }
                  : undefined
              }
              alt=''
            />
          ) : (
            <img
              src={theme.headerLogo}
              width={theme.headerLogoWidth}
              className='header-logo'
              alt=''
            />
          )}

          <MobileMenu
            logoutCall={logoutCall}
            helpDropdown={helpOverlay}
            helpOpen={mobileHelpOpen}
            onHelpOpenChange={handleMobileHelpOpenChange}
            onOpenHelpCenter={handleOpenHelpCenter}
            onOpenAiInsights={openAiInsights}
            showAiInsightsChat={showAiInsightsChat}
            showMessages={showMessages}
            theme={theme}
          />
        </Flex>
      ) : (
        <>
          {!showPreviewBanner ? (
            <CustomHeader
              decodedToken={decodedToken}
              helpDropdown={helpOverlay}
              helpOpen={helpOpen}
              menuItems={profileMenuItems}
              onAiInsightsOpen={openAiInsights}
              onHelpOpenChange={handleHelpOpenChange}
              onOpenHelpCenter={handleOpenHelpCenter}
              helpCenterOpen={helpCenterOpen}
              onProfileMenuClick={handleProfileMenuClick}
              showAiInsightsChat={showAiInsightsChat}
              showMessages={showMessages}
            />
          ) : (
            <PreviewHeader mode={effectiveMode} />
          )}
        </>
      )}

      <TourVideoModal
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        videoSrc={videoSrc}
        title='Watch 5 min tour'
      />

      <AIChatDrawer
        open={insightChatOpen}
        onClose={() => onCloseInsightChat?.()}
        pendingQuestion={insightChatQuestion}
        onPendingQuestionSent={onInsightChatQuestionSent}
        onOpenLink={(path) => navigate(path)}
      />
    </>
  );
};

export default ResponsiveHeader;
